import type { ReactNode } from "react";
import { FolderClosed, Inbox, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import type { NamedFolder } from "@app/components/FolderDialogs";
import type { FolderDrop } from "@app/utils/use-folder-drop";

type DropTargets = Pick<FolderDrop, "dropZone" | "dropStyle" | "canDropOn">;

/** Renames or deletes a classeur, from its tile or from inside it. */
export function FolderActionsMenu({
  onRename,
  onDelete,
  triggerClassName,
  triggerVariant = "ghost",
}: {
  onRename: () => void;
  onDelete: () => void;
  triggerClassName?: string;
  triggerVariant?: "ghost" | "outline";
}) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={triggerVariant}
          size="icon"
          className={triggerClassName}
          aria-label={t`Actions du classeur`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem className="cursor-pointer" onClick={onRename}>
          <Pencil className="mr-2 h-4 w-4" />
          <Trans>Renommer</Trans>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-red-600 focus:text-red-600 cursor-pointer"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          <Trans>Supprimer le classeur</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A classeur on the shelf: opens on click, takes a drop, and carries its menu when it may be changed. */
export function FolderTile({
  folder,
  count,
  drop,
  onOpen,
  onRename,
  onDelete,
}: {
  folder: NamedFolder;
  /** What it holds, e.g. « 3 cohortes ». */
  count: ReactNode;
  drop: DropTargets;
  onOpen: () => void;
  /** Both set when the session may change it. */
  onRename?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      {...drop.dropZone(folder.id)}
      className={`group relative rounded-lg border-2 border-slate-200 bg-white transition hover:border-primary/50 hover:shadow-md${drop.dropStyle(folder.id)}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 p-4 pr-11 text-left"
      >
        <div className="rounded-md bg-blue-50 p-2 text-primary">
          <FolderClosed className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-900">
            {folder.name}
          </div>
          <div className="text-sm text-slate-500">{count}</div>
        </div>
      </button>
      {onRename && onDelete && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <FolderActionsMenu
            onRename={onRename}
            onDelete={onDelete}
            triggerClassName="h-8 w-8"
          />
        </div>
      )}
    </div>
  );
}

/** The bar shown at the bottom while dragging: every classeur the item may go in, and « Sans classeur ». */
export function FolderDropBar({
  folders,
  unfiled,
  drop,
  emptyText,
}: {
  folders: NamedFolder[];
  /** The page's key for « Sans classeur ». */
  unfiled: string;
  drop: DropTargets;
  /** Said when the item may go nowhere. */
  emptyText: ReactNode;
}) {
  const targets = folders.filter((folder) => drop.canDropOn(folder.id));
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <div className="pointer-events-auto flex max-w-4xl flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur-sm">
        <span className="px-2 text-sm font-medium text-slate-600">
          <Trans>Déposer dans</Trans>
        </span>
        {targets.map((folder) => (
          <div
            key={folder.id}
            {...drop.dropZone(folder.id)}
            className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm text-slate-700 transition${drop.dropStyle(folder.id)}`}
          >
            <FolderClosed className="h-4 w-4 text-primary" />
            <span className="max-w-48 truncate">{folder.name}</span>
          </div>
        ))}
        {drop.canDropOn(unfiled) && (
          <div
            {...drop.dropZone(unfiled)}
            className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm text-slate-700 transition${drop.dropStyle(unfiled)}`}
          >
            <Inbox className="h-4 w-4 text-slate-500" />
            <Trans>Sans classeur</Trans>
          </div>
        )}
        {!drop.canDropOn(unfiled) && targets.length === 0 && (
          <span className="px-2 text-sm text-slate-500">{emptyText}</span>
        )}
      </div>
    </div>
  );
}
